// useRuleConfig — item 9/10 (Asana 1216373781992284), Rule Configuration screen (spec §10.1,
// §7.2-7.3).
//
// `base` is the read-only shape of defaultRuleset.js (factors/gates/boxes/thresholds) the screen
// renders sliders/toggles against; `local` is the UNSAVED edit buffer — nothing here is a real
// change until Save Draft fires PUT /rule-config/draft.
//
// Item 10's state machine sits on top of item 9's draft/publish: draft -> (Propose) ->
// pending_review -> (Publish, ADMIN-only server-side) -> published, or pending_review ->
// (Withdraw) -> draft. `status` reflects which of those states the tenant's draft is in; editing
// is rejected server-side while pending_review (surfaced here as a normal `error`, not special-
// cased — a 409 reads the same as any other failed write). Publish also now writes an immutable
// framework_ruleset_versions row; `versions` is that history, fetched separately since the screen
// doesn't need it on every load."

import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../api';
import { useProject } from '../../../contexts/ProjectContext';

export interface RuleBaseFactor { id: string; dimension: string; name: string; default_weight: number; }
export interface RuleBaseGate { id: string; name: string; priority: number; enabled: boolean; action: any; }
export interface RuleBaseBox { id: string; label: string; residual_risk: 'HIGH' | 'MEDIUM' | 'LOW'; target_platform_catalog: string[]; mandatory_controls?: string[]; }
export interface RuleBaseThresholds {
  dead_months: number;
  overlap_epsilon: number;
  confidence_autoconfirm: number;
  confidence_review_floor: number;
  tiebreak_epsilon: number;
  overlap_near_miss_margin: number;
  materiality_gate_band: string;
  overlap_signal_weights: { input_source: number; formula: number; output_schema: number };
}
export interface RuleBase { factors: RuleBaseFactor[]; gates: RuleBaseGate[]; boxes: RuleBaseBox[]; thresholds: RuleBaseThresholds; }

export type NumericThresholdKey = 'dead_months' | 'overlap_epsilon' | 'confidence_autoconfirm' | 'confidence_review_floor' | 'tiebreak_epsilon' | 'overlap_near_miss_margin';
export const MATERIALITY_BANDS = ['Critical', 'High', 'Moderate', 'Low'] as const;

export interface RuleOverrides {
  factors?: Record<string, { weight?: number }>;
  gates?: Record<string, { enabled?: boolean; priority?: number }>;
  boxes?: Record<string, { residual_risk?: 'HIGH' | 'MEDIUM' | 'LOW' }>;
  thresholds?: Partial<Record<NumericThresholdKey, number>> & {
    materiality_gate_band?: typeof MATERIALITY_BANDS[number];
    overlap_signal_weights?: Partial<RuleBaseThresholds['overlap_signal_weights']>;
  };
}

export interface ImpactPreview {
  total: number;
  changed: number;
  timing_changed: number;
  changes: { euc_id: string; file: string; from: string; to: string }[];
  no_op: boolean;
}

export type RulesetStatus = 'draft' | 'pending_review';
export interface Actor { id: number | string; name: string | null; email: string | null; role?: string | null; }
export interface RulesetVersionSummary {
  version: number;
  parent_version: number | null;
  overrides: RuleOverrides;
  proposed_by: Actor | null;
  proposed_at: string | null;
  published_by: Actor;
  published_at: string;
}
export interface RulesetVersionDetail extends RulesetVersionSummary { ruleset: any; }

/**
 * The tenant's EFFECTIVE ruleset — defaults deep-merged with whatever is published, at the current
 * version. Distinct from `base`, which is the UNMERGED defaults the editor needs to show
 * default-vs-overridden. Screens that DISPLAY the framework (Framework Mapping) read this; the
 * editor reads `base` + `published`. Null until the first load completes.
 */
export interface EffectiveRuleSet {
  version: number;
  published_at: string | null;
  boxes: RuleBaseBox[];
}

export interface RuleConfigState {
  loaded: boolean;
  base: RuleBase | null;
  effective: EffectiveRuleSet | null;
  published: RuleOverrides;
  publishedVersion: number;
  publishedAt: string | null;
  draft: RuleOverrides;                 // last SAVED draft (server state)
  local: RuleOverrides;                 // edit buffer, may differ from `draft` until Save Draft
  dirty: boolean;                       // local !== last-saved draft
  status: RulesetStatus;
  proposedBy: Actor | null;
  proposedAt: string | null;
  locked: boolean;                      // status === 'pending_review' — draft edits are rejected
  versions: RulesetVersionSummary[];
  versionsLoaded: boolean;
  saving: boolean;
  proposing: boolean;
  withdrawing: boolean;
  publishing: boolean;
  previewing: boolean;
  preview: ImpactPreview | null;
  previewStale: boolean;                // local edits happened since the last preview
  error: string | null;
  setFactorWeight: (id: string, weight: number) => void;
  setGateEnabled: (id: string, enabled: boolean) => void;
  setGatePriority: (id: string, priority: number) => void;
  setBoxResidualRisk: (id: string, risk: 'HIGH' | 'MEDIUM' | 'LOW') => void;
  setThreshold: (key: NumericThresholdKey, value: number) => void;
  setMaterialityGateBand: (band: typeof MATERIALITY_BANDS[number]) => void;
  setOverlapSignalWeight: (key: keyof RuleBaseThresholds['overlap_signal_weights'], value: number) => void;
  saveDraft: () => Promise<{ ok: boolean; error?: string }>;
  runPreview: () => Promise<{ ok: boolean; error?: string }>;
  propose: () => Promise<{ ok: boolean; error?: string }>;
  withdraw: () => Promise<{ ok: boolean; error?: string }>;
  publish: () => Promise<{ ok: boolean; error?: string }>;
  loadVersions: () => Promise<void>;
  loadVersionDetail: (version: number) => Promise<RulesetVersionDetail | null>;
  reload: () => Promise<void>;
}

const EMPTY: RuleOverrides = {};

function isEmpty(o: RuleOverrides): boolean {
  return !(o.factors && Object.keys(o.factors).length) && !(o.gates && Object.keys(o.gates).length)
    && !(o.boxes && Object.keys(o.boxes).length) && !(o.thresholds && Object.keys(o.thresholds).length);
}

export function useRuleConfig(): RuleConfigState {
  // QA finding 2026-08-05 (Asana 1216373781992284, subtask "Revisions to Framework Mapping"):
  // Preview Impact / Publish re-score the STORED inventory, which now lives on per-project rows —
  // without a project_id the backend fell back to the legacy tenant-wide row and 400'd with "no
  // ingested inventory". Same projectId derivation as useDecisionAudit.ts / useAttestation.ts.
  // The ruleset itself stays tenant-wide (firm-level policy), so only these two calls carry it.
  const { currentProject } = useProject();
  const projectId = currentProject?.id != null && Number.isFinite(Number(currentProject.id))
    ? Number(currentProject.id)
    : null;
  const [loaded, setLoaded] = useState(false);
  const [base, setBase] = useState<RuleBase | null>(null);
  const [effective, setEffective] = useState<EffectiveRuleSet | null>(null);
  const [published, setPublished] = useState<RuleOverrides>(EMPTY);
  const [publishedVersion, setPublishedVersion] = useState(1);
  const [publishedAt, setPublishedAt] = useState<string | null>(null);
  const [draft, setDraft] = useState<RuleOverrides>(EMPTY);
  const [local, setLocal] = useState<RuleOverrides>(EMPTY);
  const [status, setStatus] = useState<RulesetStatus>('draft');
  const [proposedBy, setProposedBy] = useState<Actor | null>(null);
  const [proposedAt, setProposedAt] = useState<string | null>(null);
  const [versions, setVersions] = useState<RulesetVersionSummary[]>([]);
  const [versionsLoaded, setVersionsLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [proposing, setProposing] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<ImpactPreview | null>(null);
  const [previewStale, setPreviewStale] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/framework/rule-config');
      const d = res?.data || {};
      setBase(d.base || null);
      setEffective(d.effective || null);
      setPublished(d.published || EMPTY);
      setPublishedVersion(d.version ?? 1);
      setPublishedAt(d.published_at ?? null);
      setDraft(d.draft || EMPTY);
      setLocal(d.draft || EMPTY);
      setStatus(d.status === 'pending_review' ? 'pending_review' : 'draft');
      setProposedBy(d.proposed_by ?? null);
      setProposedAt(d.proposed_at ?? null);
      setPreview(null);
      setPreviewStale(false);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'failed to load rule configuration');
    } finally {
      setLoaded(true);
    }
  }, []);

  const loadVersions = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/framework/rule-config/versions');
      setVersions(res?.data?.items || []);
    } catch {
      // Versions panel is supplementary — a failed fetch here shouldn't block the editing screen.
      setVersions([]);
    } finally {
      setVersionsLoaded(true);
    }
  }, []);

  const loadVersionDetail = useCallback(async (version: number): Promise<RulesetVersionDetail | null> => {
    try {
      const res = await apiClient.get(`/api/framework/rule-config/versions/${version}`);
      return res?.data || null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const touch = useCallback((updater: (prev: RuleOverrides) => RuleOverrides) => {
    setLocal((prev) => updater(prev));
    setPreviewStale(true);
  }, []);

  const setFactorWeight = useCallback((id: string, weight: number) => {
    touch((prev) => ({ ...prev, factors: { ...prev.factors, [id]: { ...(prev.factors?.[id] || {}), weight } } }));
  }, [touch]);

  const setGateEnabled = useCallback((id: string, enabled: boolean) => {
    touch((prev) => ({ ...prev, gates: { ...prev.gates, [id]: { ...(prev.gates?.[id] || {}), enabled } } }));
  }, [touch]);

  const setGatePriority = useCallback((id: string, priority: number) => {
    touch((prev) => ({ ...prev, gates: { ...prev.gates, [id]: { ...(prev.gates?.[id] || {}), priority } } }));
  }, [touch]);

  const setBoxResidualRisk = useCallback((id: string, risk: 'HIGH' | 'MEDIUM' | 'LOW') => {
    touch((prev) => ({ ...prev, boxes: { ...prev.boxes, [id]: { residual_risk: risk } } }));
  }, [touch]);

  const setThreshold = useCallback((key: NumericThresholdKey, value: number) => {
    touch((prev) => ({ ...prev, thresholds: { ...prev.thresholds, [key]: value } }));
  }, [touch]);

  const setMaterialityGateBand = useCallback((band: typeof MATERIALITY_BANDS[number]) => {
    touch((prev) => ({ ...prev, thresholds: { ...prev.thresholds, materiality_gate_band: band } }));
  }, [touch]);

  const setOverlapSignalWeight = useCallback((key: any, value: number) => {
    touch((prev) => ({
      ...prev,
      thresholds: { ...prev.thresholds, overlap_signal_weights: { ...prev.thresholds?.overlap_signal_weights, [key]: value } },
    }));
  }, [touch]);

  const saveDraft = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await apiClient.put('/api/framework/rule-config/draft', { overrides: local });
      setDraft(local);
      return { ok: true };
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'failed to save draft';
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setSaving(false);
    }
  }, [local]);

  // Item 10: propose -> pending_review. Refuses server-side on an empty draft or a double-propose
  // (surfaced as `error`, same as any other failed write).
  const propose = useCallback(async () => {
    setProposing(true);
    setError(null);
    try {
      const res = await apiClient.post('/api/framework/rule-config/propose', {});
      setStatus('pending_review');
      setProposedAt(res?.data?.proposed_at ?? new Date().toISOString());
      return { ok: true };
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'failed to propose the draft for review';
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setProposing(false);
    }
  }, []);

  // Pulls a pending_review draft back to editable without publishing it.
  const withdraw = useCallback(async () => {
    setWithdrawing(true);
    setError(null);
    try {
      await apiClient.post('/api/framework/rule-config/withdraw', {});
      setStatus('draft');
      setProposedBy(null);
      setProposedAt(null);
      return { ok: true };
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'failed to withdraw the proposal';
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setWithdrawing(false);
    }
  }, []);

  const runPreview = useCallback(async () => {
    setPreviewing(true);
    setError(null);
    try {
      const res = await apiClient.post('/api/framework/rule-config/impact-preview', { overrides: local, project_id: projectId });
      setPreview(res?.data || null);
      setPreviewStale(false);
      return { ok: true };
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'failed to compute impact preview';
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setPreviewing(false);
    }
  }, [local, projectId]);

  // Server-gated to the ADMIN role (the TSG-review step) and to status === 'pending_review' — a
  // 403 or 409 here is expected, not a bug, until both conditions hold.
  const publish = useCallback(async () => {
    setPublishing(true);
    setError(null);
    try {
      const res = await apiClient.post('/api/framework/rule-config/publish', { overrides: local, project_id: projectId });
      const d = res?.data || {};
      setPublished(local);
      setPublishedVersion(d.version ?? publishedVersion + 1);
      setPublishedAt(d.published_at ?? new Date().toISOString());
      setDraft(EMPTY);
      setLocal(EMPTY);
      setStatus('draft');
      setProposedBy(null);
      setProposedAt(null);
      setPreview(null);
      setPreviewStale(false);
      setVersionsLoaded(false); // stale after publish — a subsequent loadVersions() will re-fetch
      return { ok: true };
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'failed to publish';
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setPublishing(false);
    }
  }, [local, publishedVersion, projectId]);

  return {
    loaded, base, effective, published, publishedVersion, publishedAt, draft, local,
    dirty: JSON.stringify(local) !== JSON.stringify(draft),
    status, proposedBy, proposedAt, locked: status === 'pending_review',
    versions, versionsLoaded,
    saving, proposing, withdrawing, publishing, previewing, preview, previewStale, error,
    setFactorWeight, setGateEnabled, setGatePriority, setBoxResidualRisk, setThreshold, setMaterialityGateBand, setOverlapSignalWeight,
    saveDraft, runPreview, propose, withdraw, publish, loadVersions, loadVersionDetail, reload,
  };
}

export { isEmpty as isEmptyOverrides };
